import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import type { GridRowParams, MuiEvent } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { buildRemoteConfigColumns } from 'src/constants/remote-config/remoteConfigGrid'
import type { RemoteConfig } from 'src/interfaces/remoteConfig'
import { getRemoteConfigsLoading } from 'src/selectors/remoteConfig'
import { EmptyRemoteConfigs } from './EmptyRemoteConfigs'

interface Props {
  remoteConfigs: RemoteConfig[]
  searchQuery: string
  canManage: boolean
  onOpenRemoteConfig(remoteConfig: RemoteConfig): void
  onEditRemoteConfig(remoteConfig: RemoteConfig): void
  onDeleteRemoteConfig(remoteConfig: RemoteConfig): void
}

const getRowId = (row: RemoteConfig): string => {
  return row.id
}

const RemoteConfigsTableComponent = ({
  remoteConfigs,
  searchQuery,
  canManage,
  onOpenRemoteConfig,
  onEditRemoteConfig,
  onDeleteRemoteConfig,
}: Props) => {
  const isLoading = useSelector(getRemoteConfigsLoading)

  const columns = useMemo(() => {
    return buildRemoteConfigColumns({
      canManage,
      onEdit: onEditRemoteConfig,
      onDelete: onDeleteRemoteConfig,
    })
  }, [canManage, onEditRemoteConfig, onDeleteRemoteConfig])

  const handleRowClick = useCallback(
    (params: GridRowParams<RemoteConfig>, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
      // The ⋯ menu renders in a portal, and React still bubbles its clicks here; only real row clicks open the key.
      if (!event.currentTarget.contains(event.target as Node)) {
        return
      }

      onOpenRemoteConfig(params.row)
    },
    [onOpenRemoteConfig],
  )

  const EmptyElement = useMemo(() => {
    const BoundEmptyRemoteConfigs = () => {
      return <EmptyRemoteConfigs searchQuery={searchQuery} />
    }

    return BoundEmptyRemoteConfigs
  }, [searchQuery])

  return (
    <GridTable<RemoteConfig>
      rows={remoteConfigs}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      fillHeight
      EmptyElement={EmptyElement}
      className={tw`[& .MuiDataGrid-row]:cursor-pointer`}
      onRowClick={handleRowClick}
      tableDataTestId="remote-config-table"
      rowDataTestIdPrefix="remote-config"
    />
  )
}

export const RemoteConfigsTable = memo(RemoteConfigsTableComponent)
